"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSession } from "next-auth/react";

export type LocationTypeRecord = {
  id: string;
  title: string;
  icon: string;
};

type LocationTypesContextValue = {
  /** Map of `location_types.id` → row. Loaded once per authenticated session mount. */
  typesById: ReadonlyMap<string, LocationTypeRecord>;
  isLoading: boolean;
  error: string | null;
  getType: (typeId: string) => LocationTypeRecord | undefined;
  getIcon: (typeId: string) => string | undefined;
  refresh: () => void;
};

const LocationTypesContext = createContext<LocationTypesContextValue | null>(null);

export function LocationTypesProvider({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  const [typesById, setTypesById] = useState<ReadonlyMap<string, LocationTypeRecord>>(
    () => new Map(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (status === "unauthenticated") {
      setTypesById(new Map());
      setIsLoading(false);
      setError(null);
      return;
    }
    if (status === "loading") {
      setIsLoading(true);
      return;
    }

    const ac = new AbortController();
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const res = await fetch("/api/location-types", {
          credentials: "same-origin",
          signal: ac.signal,
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as {
            error?: string;
          } | null;
          throw new Error(body?.error ?? res.statusText);
        }
        const data = (await res.json()) as { types?: LocationTypeRecord[] };
        const list = data.types ?? [];
        if (!ac.signal.aborted) {
          setTypesById(new Map(list.map((t) => [t.id, t])));
        }
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        if (!ac.signal.aborted) {
          setError(e instanceof Error ? e.message : "Failed to load types");
          setTypesById(new Map());
        }
      } finally {
        if (!ac.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [status, refreshToken]);

  const getType = useCallback((typeId: string) => typesById.get(typeId), [typesById]);

  const getIcon = useCallback((typeId: string) => typesById.get(typeId)?.icon, [typesById]);

  const refresh = useCallback(() => {
    if (status !== "authenticated") return;
    setRefreshToken((n) => n + 1);
  }, [status]);

  const value = useMemo(
    (): LocationTypesContextValue => ({
      typesById,
      isLoading,
      error,
      getType,
      getIcon,
      refresh,
    }),
    [typesById, isLoading, error, getType, getIcon, refresh],
  );

  return <LocationTypesContext.Provider value={value}>{children}</LocationTypesContext.Provider>;
}

export function useLocationTypes(): LocationTypesContextValue {
  const ctx = useContext(LocationTypesContext);
  if (!ctx) {
    throw new Error("useLocationTypes must be used within LocationTypesProvider");
  }
  return ctx;
}
